/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Guard against invalid cart/change requests triggered by quantity inputs outside cart line-item contexts.
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.closest('line-item-quantity')) return;

  // Only line-item quantity inputs should carry data-line in this theme.
  if (target.getAttribute('data-line')) return;

  event.stopImmediatePropagation();
  event.stopPropagation();
}, true);

if (!window.customElements.get('b2b-bulk-order')) {
  class B2BBulkOrder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.successToast = this.querySelector('[data-success-toast]');
      this.successToastCloseButton = this.querySelector('[data-success-toast-close]');
      this._successToastTimeout = null;
      this._successToastHideTimeout = null;
      this._boundOnCloseSuccessToast = this.hideSuccessToast.bind(this);
      this._boundOnSubmit = this.onSubmit.bind(this);

      if (this.successToastCloseButton) {
        this.successToastCloseButton.addEventListener('click', this._boundOnCloseSuccessToast);
      }

      this.form.addEventListener('submit', this._boundOnSubmit);
    }

    disconnectedCallback() {
      clearTimeout(this._successToastTimeout);
      clearTimeout(this._successToastHideTimeout);

      if (this.successToastCloseButton && this._boundOnCloseSuccessToast) {
        this.successToastCloseButton.removeEventListener('click', this._boundOnCloseSuccessToast);
      }

      if (this.form && this._boundOnSubmit) {
        this.form.removeEventListener('submit', this._boundOnSubmit);
      }
    }

    clearMessages() {
      this.querySelectorAll('[data-row-error]').forEach((rowError) => {
        rowError.hidden = true;
        rowError.textContent = '';
      });

      const globalError = this.querySelector('[data-global-error]');

      if (globalError) {
        globalError.hidden = true;
        globalError.textContent = '';
      }
    }

    setRowError(input, message) {
      const row = input.closest('[data-product-row]');
      const rowError = row ? row.querySelector('[data-row-error]') : null;
      if (!rowError) return;

      rowError.hidden = false;
      rowError.textContent = message;
    }

    setGlobalError(message) {
      const globalError = this.querySelector('[data-global-error]');
      if (!globalError) return;

      globalError.hidden = false;
      globalError.textContent = message;
    }

    showSuccessToast() {
      if (!this.successToast) return;

      clearTimeout(this._successToastTimeout);
      clearTimeout(this._successToastHideTimeout);

      this.successToast.hidden = false;
      requestAnimationFrame(() => {
        this.successToast.classList.add('is-visible');
      });

      this._successToastTimeout = setTimeout(() => {
        this.hideSuccessToast();
      }, 3000);
    }

    hideSuccessToast() {
      if (!this.successToast || this.successToast.hidden) return;

      clearTimeout(this._successToastTimeout);
      this.successToast.classList.remove('is-visible');

      this._successToastHideTimeout = setTimeout(() => {
        this.successToast.hidden = true;
      }, 200);
    }

    async onSubmit(event) {
      event.preventDefault();
      this.clearMessages();
      const submitButton = this.form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.setAttribute('disabled', 'disabled');
        submitButton.setAttribute('aria-busy', 'true');
      }

      const inputs = Array.from(this.form.querySelectorAll('input[name="quantity"]'));
      const items = [];
      let hasErrors = false;

      inputs.forEach((input) => {
        const rawValue = parseInt(input.value, 10);
        const quantity = Number.isNaN(rawValue) ? 0 : rawValue;

        if (quantity <= 0) return;

        const variantId = parseInt(input.dataset.variantId || '', 10);
        const min = parseInt(input.dataset.min || '1', 10);
        const step = parseInt(input.dataset.step || '1', 10);
        const max = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : null;
        const unavailable = input.dataset.unavailable === 'true';

        if (!variantId || unavailable) {
          hasErrors = true;
          this.setRowError(input, 'This item is unavailable.');
          return;
        }

        if (quantity < min) {
          hasErrors = true;
          this.setRowError(input, `Minimum quantity for this item is ${min}.`);
          return;
        }

        if ((quantity - min) % step !== 0) {
          hasErrors = true;
          this.setRowError(input, `Quantity must increase in steps of ${step}.`);
          return;
        }

        if (max !== null && quantity > max) {
          hasErrors = true;
          this.setRowError(input, `Maximum available quantity is ${max}.`);
          return;
        }

        items.push({ id: variantId, quantity });
      });

      if (!items.length) {
        hasErrors = true;
        this.setGlobalError('Select quantity for at least one product.');
      }

      if (hasErrors) {
        if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-busy');
        }
        return;
      }

      try {
        const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: ['mini-cart']
          })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || 'Unable to add items to cart.');
        }

        inputs.forEach((input) => {
          input.value = input.dataset.initialQuantity || '0';
        });

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        this.showSuccessToast();

        try {
          const cartResponse = await fetch(`${window.themeVariables.routes.cartUrl}.js`);

          if (!cartResponse.ok) {
            throw new Error('Unable to refresh cart state.');
          }

          const cartContent = await cartResponse.json();

          document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cart: cartContent }
          }));

          cartContent.sections = responseJson.sections;
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: window.themeVariables.settings.cartType === 'drawer'
            }
          }));
        } catch (refreshError) {
          console.warn('B2B quick order: items were added, but cart UI refresh failed.', refreshError);
        }
      } catch (error) {
        const message = error && error.message ? error.message : 'Unable to add items to cart.';
        this.setGlobalError(message);
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: {
            status: 'error',
            error: message
          }
        }));
      } finally {
        if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.removeAttribute('aria-busy');
        }
      }
    }
  }

  window.customElements.define('b2b-bulk-order', B2BBulkOrder);
}

(() => {
  const FREE_GIFT_PROPERTY = '_free_gift';
  const GIFT_OFFSET_PROPERTY = '_gift_offset';
  const DEBOUNCE_MS = 120;
  const CART_INTERACTION_DELAY_MS = 220;
  const CART_RECONCILE_RETRY_MS = 450;
  const MUTATION_SUPPRESS_MS = 700;
  const MAX_SYNC_PASSES = 6;
  const DEBUG_PREFIX = '[FreeGiftSync]';
  const FREE_GIFT_SYNC_SOURCE = 'free-gift-sync';

  class BuyXGetSameProductFreeSync {
    constructor() {
      this.variantOffsetCache = new Map();
      this.syncTimer = null;
      this.syncInFlight = false;
      this.syncQueued = false;
      this.lastKnownSignature = '';
      this.lastQueuedCart = null;
      this.storageKey = 'theme:gift-offset-cache';
      this.domObserver = null;
      this.pendingInteractionTimer = null;
      this.suppressMutationSyncUntil = 0;
      this.checkoutBlocked = false;
      this.pendingGiftSyncLoaderVariantId = null;
      this.giftSyncLoaderTimer = null;
    }

    init() {
      this.loadOffsetCache();
      this.collectOffsetsFromDom();
      this.bindCartInteractionListeners();
      this.observeDomMutations();

      document.addEventListener('variant:changed', this.handleVariantChanged.bind(this));
      document.addEventListener('submit', this.handleProductSubmit.bind(this), true);
      document.addEventListener('line-item-quantity:change:start', this.handleLineItemQuantityChangeStart.bind(this));
      document.addEventListener('line-item-quantity:change:end', this.handleLineItemQuantityChangeEnd.bind(this));
      document.documentElement.addEventListener('cart:updated', this.handleCartEvent.bind(this));
      document.documentElement.addEventListener('cart:refresh', this.handleCartEvent.bind(this));
      window.addEventListener('pageshow', () => this.scheduleSync());
      window.addEventListener('popstate', () => this.scheduleSync(null, CART_INTERACTION_DELAY_MS));

      this.scheduleDomRescan();
      this.scheduleSync();
      this.scheduleSync(null, 1200);
    }

    handleVariantChanged(event) {
      const form = event.target;
      const variantId = Number(event?.detail?.variant?.id);
      const offset = this.readGiftOffset(form instanceof HTMLElement ? (form.closest('form') || form) : null);

      if (variantId && offset > 0) {
        this.variantOffsetCache.set(variantId, offset);
        this.persistOffsetCache();
        console.log(DEBUG_PREFIX, 'variant:changed cached offset', { variantId, offset });
      }
    }

    handleProductSubmit(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') {
        return;
      }

      const variantId = Number(new FormData(form).get('id'));
      const offset = this.readGiftOffset(form);

      if (variantId && offset > 0) {
        this.variantOffsetCache.set(variantId, offset);
        this.persistOffsetCache();
        console.log(DEBUG_PREFIX, 'product form submit cached offset', { variantId, offset });
      }

      this.scheduleSync(null, CART_RECONCILE_RETRY_MS);
    }

    handleCartEvent(event) {
      if (event?.detail?.source === FREE_GIFT_SYNC_SOURCE) {
        return;
      }

      if (event?.detail?.cart) {
        this.lastQueuedCart = event.detail.cart;
      }

      this.collectOffsetsFromDom();
      this.scheduleDomRescan(event?.detail?.replacementDelay);
      this.scheduleGiftSyncLoader(event?.detail?.replacementDelay);
      this.scheduleSync(this.lastQueuedCart, event?.detail?.cart ? 0 : DEBOUNCE_MS);
    }

    scheduleDomRescan(delay = 0) {
      window.setTimeout(() => this.collectOffsetsFromDom(), Math.max(0, Number(delay) || 0) + 50);
    }

    scheduleSync(cart = null, delay = DEBOUNCE_MS) {
      if (cart) {
        this.lastQueuedCart = cart;
      }

      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => {
        this.syncTimer = null;
        this.sync().catch((error) => {
          console.error('Free gift sync failed.', error);
        }).finally(() => {
          this.refreshCheckoutBlockState();
        });
      }, Math.max(0, Number(delay) || 0));
    }

    async sync() {
      if (this.syncInFlight) {
        this.syncQueued = true;
        return;
      }

      this.syncInFlight = true;
      let hasCartChanges = false;

      try {
        let cart = this.lastQueuedCart || await this.fetchCart();
        let passCount = 0;
        this.lastQueuedCart = null;

        while (cart && passCount < MAX_SYNC_PASSES) {
          passCount += 1;
          this.collectOffsetsFromDom();

          const operations = this.buildOperations(cart);
          const signature = this.getCartSignature(cart);
          console.log(DEBUG_PREFIX, 'sync cycle', {
            itemCount: cart.item_count,
            passCount,
            signature,
            operations
          });

          if (operations.length === 0) {
            this.lastKnownSignature = signature;
            this.releaseGiftSyncLoader();
            if (hasCartChanges) {
              this.dispatchCartEvents(cart);
            }
            break;
          }

          await this.applyOperations(operations);
          hasCartChanges = true;
          cart = await this.fetchCart();
          this.lastQueuedCart = cart;
        }

        if (passCount >= MAX_SYNC_PASSES) {
          console.warn(DEBUG_PREFIX, 'stopped after reaching max sync passes', { passCount, cart });
          this.releaseGiftSyncLoader();
          if (hasCartChanges && cart) {
            this.dispatchCartEvents(cart);
          }
        }
      } finally {
        this.syncInFlight = false;

        if (this.syncQueued) {
          this.syncQueued = false;
          this.scheduleSync();
        } else {
          this.releaseGiftSyncLoader();
        }

        this.refreshCheckoutBlockState();
      }
    }

    buildOperations(cart) {
      const signature = this.getCartSignature(cart);

      if (signature === this.lastKnownSignature) {
        return [];
      }

      const groups = new Map();

      (cart.items || []).forEach((item) => {
        const variantId = Number(item.variant_id);

        if (!variantId) {
          return;
        }

        if (!groups.has(variantId)) {
          groups.set(variantId, {
            variantId,
            offset: this.getGiftOffsetForItem(item),
            mainQty: 0,
            gifts: []
          });
        }

        const group = groups.get(variantId);
        const offset = this.getGiftOffsetForItem(item);

        if (!group.offset && offset > 0) {
          group.offset = offset;
        }

        if (this.isFreeGiftItem(item)) {
          group.gifts.push(item);
        } else {
          group.mainQty += Number(item.quantity) || 0;
        }
      });

      const operations = [];

      groups.forEach((group) => {
        if (group.offset <= 0) {
          console.log(DEBUG_PREFIX, 'skipping variant with no gift offset', {
            variantId: group.variantId,
            mainQty: group.mainQty
          });
          return;
        }

        const desiredGiftQty = Math.floor(group.mainQty / group.offset);
        const totalGiftQty = group.gifts.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
        const [primaryGift, ...duplicateGifts] = group.gifts;
        console.log(DEBUG_PREFIX, 'evaluated variant', {
          variantId: group.variantId,
          offset: group.offset,
          mainQty: group.mainQty,
          totalGiftQty,
          desiredGiftQty,
          giftKeys: group.gifts.map((item) => item.key)
        });

        duplicateGifts.forEach((item) => {
          operations.push({
            type: 'change',
            key: item.key,
            quantity: 0
          });
        });

        if (desiredGiftQty <= 0) {
          if (primaryGift) {
            operations.push({
              type: 'change',
              key: primaryGift.key,
              quantity: 0
            });
          }

          return;
        }

        if (!primaryGift) {
          operations.push({
            type: 'add',
            variantId: group.variantId,
            quantity: desiredGiftQty,
            offset: group.offset
          });
          return;
        }

        if (totalGiftQty !== desiredGiftQty || Number(primaryGift.quantity) !== desiredGiftQty) {
          operations.push({
            type: 'change',
            key: primaryGift.key,
            quantity: desiredGiftQty
          });
        }
      });

      return operations;
    }

    async applyOperations(operations) {
      for (const operation of operations) {
        console.log(DEBUG_PREFIX, 'applying operation', operation);

        if (operation.type === 'add') {
          await this.addGiftLine(operation.variantId, operation.quantity, operation.offset);
          continue;
        }

        if (operation.type === 'change') {
          await this.changeLine(operation.key, operation.quantity);
        }
      }
    }

    async addGiftLine(variantId, quantity, offset) {
      if (quantity <= 0) {
        return;
      }

      console.log(DEBUG_PREFIX, 'adding free gift line', { variantId, quantity, offset });

      const response = await fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity,
            properties: {
              [FREE_GIFT_PROPERTY]: 'true',
              [GIFT_OFFSET_PROPERTY]: String(offset)
            }
          }]
        })
      });

      if (!response.ok) {
        const responseJson = await response.json().catch(() => ({}));
        console.error(DEBUG_PREFIX, 'add gift line failed', {
          variantId,
          quantity,
          offset,
          responseJson
        });
        throw new Error(responseJson.description || 'Unable to add free gift line.');
      }

      console.log(DEBUG_PREFIX, 'added free gift line successfully', { variantId, quantity, offset });
    }

    async changeLine(key, quantity) {
      console.log(DEBUG_PREFIX, 'changing gift line', { key, quantity });

      const response = await fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        const responseJson = await response.json().catch(() => ({}));
        console.error(DEBUG_PREFIX, 'change gift line failed', {
          key,
          quantity,
          responseJson
        });
        throw new Error(responseJson.description || 'Unable to update free gift line.');
      }

      console.log(DEBUG_PREFIX, 'changed gift line successfully', { key, quantity });
    }

    async fetchCart() {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error('Unable to load cart state.');
      }

      return response.json();
    }

    dispatchCartEvents(cart) {
      this.suppressMutationSyncUntil = Date.now() + MUTATION_SUPPRESS_MS;
      this.lastQueuedCart = cart;
      this.scheduleDomRescan(80);

      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart, source: FREE_GIFT_SYNC_SOURCE }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, source: FREE_GIFT_SYNC_SOURCE }
      }));
    }

    bindCartInteractionListeners() {
      document.addEventListener('click', this.handleDocumentClick.bind(this), true);
      document.addEventListener('change', this.handleQuantityInputChange.bind(this), true);
      document.addEventListener('input', this.handleQuantityInputChange.bind(this), true);
      document.addEventListener('submit', this.handleCartFormSubmit.bind(this), true);
    }

    handleLineItemQuantityChangeStart(event) {
      this.setCheckoutBlocked(true);
      this.pendingGiftSyncLoaderVariantId = this.getGiftSyncLoaderVariantId(event);
    }

    handleLineItemQuantityChangeEnd(event) {
      if (event?.detail?.cart) {
        this.lastQueuedCart = event.detail.cart;
      }

      if (this.pendingGiftSyncLoaderVariantId) {
        this.applyGiftSyncLoaderToDom(this.pendingGiftSyncLoaderVariantId);
      }

      this.collectOffsetsFromDom();
      this.scheduleSync(this.lastQueuedCart, event?.detail?.cart ? 0 : DEBOUNCE_MS);
    }

    handleDocumentClick(event) {
      const trigger = event.target instanceof Element ? event.target.closest('a, button') : null;

      if (!trigger) {
        return;
      }

      const href = trigger.getAttribute('href') || '';
      const isCartChangeLink = href.includes('/cart/change');
      const isQuantityButton = trigger.closest('.quantity-selector') || trigger.closest('line-item-quantity');
      const isRemoveButton = trigger.matches('[href*="/cart/change"], [href*="/cart"], .line-item__remove-button');

      if (isCartChangeLink || isQuantityButton || isRemoveButton) {
        this.schedulePostInteractionSync();
      }
    }

    handleQuantityInputChange(event) {
      const input = event.target;

      if (!(input instanceof HTMLInputElement)) {
        return;
      }

      if (input.name === 'updates[]' || input.closest('line-item-quantity') || input.closest('.quantity-selector')) {
        this.schedulePostInteractionSync();
      }
    }

    handleCartFormSubmit(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      const action = form.getAttribute('action') || '';
      const isCartForm = action.includes('/cart') || form.querySelector('input[name="updates[]"]');
      const isCheckoutSubmit = event.submitter instanceof HTMLElement && event.submitter.getAttribute('name') === 'checkout';

      if (isCheckoutSubmit && this.isCheckoutBlocked()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isCartForm) {
        this.schedulePostInteractionSync();
      }
    }

    schedulePostInteractionSync() {
      this.setCheckoutBlocked(true);
      window.clearTimeout(this.pendingInteractionTimer);
      this.pendingInteractionTimer = window.setTimeout(() => {
        this.pendingInteractionTimer = null;
        this.collectOffsetsFromDom();
        this.scheduleSync(null, CART_INTERACTION_DELAY_MS);
        this.refreshCheckoutBlockState();
      }, 40);
    }

    isCheckoutBlocked() {
      return this.checkoutBlocked || this.syncInFlight || this.syncQueued || this.pendingInteractionTimer !== null || this.syncTimer !== null;
    }

    setCheckoutBlocked(blocked) {
      this.checkoutBlocked = Boolean(blocked);
      this.updateCheckoutButtons();
    }

    refreshCheckoutBlockState() {
      this.checkoutBlocked = this.syncInFlight || this.syncQueued || this.pendingInteractionTimer !== null || this.syncTimer !== null;
      this.updateCheckoutButtons();
    }

    updateCheckoutButtons() {
      const isBlocked = this.isCheckoutBlocked();

      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) {
          return;
        }

        button.disabled = isBlocked;
        button.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
        button.setAttribute('aria-busy', isBlocked ? 'true' : 'false');

        if (isBlocked) {
          button.classList.add('is-disabled');
        } else {
          button.classList.remove('is-disabled');
        }
      });
    }

    getGiftSyncLoaderVariantId(event) {
      const lineItem = event?.target instanceof Element ? event.target.closest('line-item, .line-item') : null;

      if (!(lineItem instanceof Element)) {
        return null;
      }

      if (lineItem.getAttribute('data-free-gift') === 'true') {
        return null;
      }

      const offset = Number(lineItem.getAttribute('data-gift-offset')) || 0;
      const variantId = Number(lineItem.getAttribute('data-cart-item-variant-id')) || 0;

      if (offset <= 0 || !variantId) {
        return null;
      }

      return variantId;
    }

    scheduleGiftSyncLoader(delay = 0) {
      if (!this.pendingGiftSyncLoaderVariantId) {
        return;
      }

      window.clearTimeout(this.giftSyncLoaderTimer);
      this.giftSyncLoaderTimer = window.setTimeout(() => {
        this.giftSyncLoaderTimer = null;
        this.applyGiftSyncLoaderToDom(this.pendingGiftSyncLoaderVariantId);
      }, Math.max(0, Number(delay) || 0) + 40);
    }

    applyGiftSyncLoaderToDom(variantId) {
      if (!variantId) {
        return;
      }

      document.querySelectorAll(`[data-cart-item-variant-id="${variantId}"][data-free-gift="false"]`).forEach((lineItem) => {
        const loader = lineItem.querySelector('.line-item__loader');
        const spinner = loader?.querySelector('.line-item__loader-spinner');
        const mark = loader?.querySelector('.line-item__loader-mark');

        if (!(loader instanceof HTMLElement) || !(spinner instanceof HTMLElement) || !(mark instanceof HTMLElement)) {
          return;
        }

        loader.hidden = false;
        spinner.hidden = false;
        spinner.style.opacity = '1';
        spinner.style.transform = 'translateY(0)';
        mark.hidden = true;
        mark.style.opacity = '';
        mark.style.transform = '';
      });
    }

    releaseGiftSyncLoader() {
      window.clearTimeout(this.giftSyncLoaderTimer);
      this.giftSyncLoaderTimer = null;

      if (!this.pendingGiftSyncLoaderVariantId) {
        return;
      }

      document.querySelectorAll(`[data-cart-item-variant-id="${this.pendingGiftSyncLoaderVariantId}"][data-free-gift="false"] .line-item__loader`).forEach((loader) => {
        if (loader instanceof HTMLElement) {
          loader.hidden = true;
        }
      });

      this.pendingGiftSyncLoaderVariantId = null;
    }

    observeDomMutations() {
      if (!('MutationObserver' in window)) {
        return;
      }

      this.domObserver = new MutationObserver((mutations) => {
        if (Date.now() < this.suppressMutationSyncUntil) {
          return;
        }

        const hasRelevantChange = mutations.some((mutation) => {
          if (mutation.type === 'attributes') {
            return mutation.target instanceof Element && mutation.target.matches('[data-cart-item-variant-id], form[is="product-form"]');
          }

          return Array.from(mutation.addedNodes).some((node) => {
            return node instanceof Element && (node.matches('[data-cart-item-variant-id], form[is="product-form"], line-item') || node.querySelector('[data-cart-item-variant-id], form[is="product-form"], line-item'));
          });
        });

        if (hasRelevantChange) {
          this.collectOffsetsFromDom();
          this.scheduleSync(null, DEBOUNCE_MS);
        }
      });

      this.domObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-item-variant-id', 'data-gift-offset', 'value']
      });
    }

    collectOffsetsFromDom() {
      document.querySelectorAll('form[is="product-form"]').forEach((form) => {
        const variantId = Number(form.querySelector('input[name="id"]')?.value);
        const offset = this.readGiftOffset(form);

        if (variantId && offset > 0) {
          this.variantOffsetCache.set(variantId, offset);
          console.log(DEBUG_PREFIX, 'collected offset from product form', { variantId, offset });
        }
      });

      document.querySelectorAll('[data-cart-item-variant-id][data-gift-offset]').forEach((item) => {
        const variantId = Number(item.getAttribute('data-cart-item-variant-id'));
        const offset = Number(item.getAttribute('data-gift-offset'));

        if (variantId && offset > 0) {
          this.variantOffsetCache.set(variantId, offset);
          console.log(DEBUG_PREFIX, 'collected offset from cart DOM', { variantId, offset });
        }
      });

      document.querySelectorAll('script[data-gift-config]').forEach((node) => {
        try {
          const config = JSON.parse(node.textContent || '{}');
          const variantId = Number(config.selectedVariantId);
          const offset = Number(config.giftOffset);

          if (variantId && offset > 0) {
            this.variantOffsetCache.set(variantId, offset);
            console.log(DEBUG_PREFIX, 'collected offset from gift config', { variantId, offset, config });
          }
        } catch (error) {
          console.warn('Invalid gift config JSON.', error);
        }
      });

      this.persistOffsetCache();
    }

    readGiftOffset(element) {
      if (!(element instanceof Element)) {
        return 0;
      }

      const input = element.querySelector('[data-gift-offset-input]') || element.closest('form')?.querySelector('[data-gift-offset-input]');
      return Math.max(0, Number(input?.value) || 0);
    }

    getGiftOffsetForItem(item) {
      const variantId = Number(item?.variant_id);
      const propertyOffset = Number(item?.properties?.[GIFT_OFFSET_PROPERTY]);

      if (propertyOffset > 0) {
        this.variantOffsetCache.set(variantId, propertyOffset);
        console.log(DEBUG_PREFIX, 'using gift offset from line item property', { variantId, propertyOffset });
        return propertyOffset;
      }

      const cachedOffset = this.variantOffsetCache.get(variantId) || 0;

      if (!cachedOffset) {
        console.log(DEBUG_PREFIX, 'no cached gift offset for variant', { variantId, item });
      }

      return cachedOffset;
    }

    isFreeGiftItem(item) {
      const rawValue = item?.properties?.[FREE_GIFT_PROPERTY];
      return rawValue === true || rawValue === 'true' || rawValue === '1';
    }

    loadOffsetCache() {
      try {
        const rawValue = window.localStorage.getItem(this.storageKey);

        if (!rawValue) {
          return;
        }

        const parsed = JSON.parse(rawValue);

        Object.entries(parsed).forEach(([variantId, offset]) => {
          const parsedVariantId = Number(variantId);
          const parsedOffset = Number(offset);

          if (parsedVariantId && parsedOffset > 0) {
            this.variantOffsetCache.set(parsedVariantId, parsedOffset);
          }
        });
      } catch (error) {
        console.warn('Unable to restore gift offset cache.', error);
      }
    }

    persistOffsetCache() {
      try {
        const serialized = {};

        this.variantOffsetCache.forEach((offset, variantId) => {
          if (offset > 0) {
            serialized[variantId] = offset;
          }
        });

        window.localStorage.setItem(this.storageKey, JSON.stringify(serialized));
      } catch (error) {
        // Ignore storage failures.
      }
    }

    getCartSignature(cart) {
      return (cart.items || []).map((item) => [
        item.key,
        item.variant_id,
        item.quantity,
        item.final_line_price,
        item.original_line_price,
        this.isFreeGiftItem(item) ? 'gift' : 'main'
      ].join(':')).join('|');
    }
  }

  const init = () => {
    const sync = new BuyXGetSameProductFreeSync();
    sync.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

(() => {
  const MEDIA_SELECTOR = '[data-reel-media]';
  let navUpdateScheduled = false;
  let suppressExclusiveSync = false;

  const setMediaPlayingState = (mediaContainer, isPlaying) => {
    if (!(mediaContainer instanceof HTMLElement)) return;
    mediaContainer.classList.toggle('is-playing', Boolean(isPlaying));
  };

  const resetExternalMedia = (mediaContainer) => {
    if (!(mediaContainer instanceof HTMLElement)) return;

    const externalContainer = mediaContainer.querySelector('.video-reel-card__external');
    if (!(externalContainer instanceof HTMLElement)) return;

    externalContainer.innerHTML = '';
    setMediaPlayingState(mediaContainer, false);
  };

  const stopCardMedia = (mediaContainer) => {
    if (!(mediaContainer instanceof HTMLElement)) return;

    const video = mediaContainer.querySelector('video[data-reel-video]');

    if (video instanceof HTMLVideoElement) {
      video.pause();
      video.controls = false;
      setMediaPlayingState(mediaContainer, false);
      return;
    }

    resetExternalMedia(mediaContainer);
  };

  const stopOtherCardMedia = (activeMediaContainer) => {
    if (suppressExclusiveSync) return;

    suppressExclusiveSync = true;

    try {
      document.querySelectorAll(MEDIA_SELECTOR).forEach((mediaContainer) => {
        if (!(mediaContainer instanceof HTMLElement) || mediaContainer === activeMediaContainer) return;
        stopCardMedia(mediaContainer);
      });
    } finally {
      suppressExclusiveSync = false;
    }
  };

  const startMedia = async (mediaContainer) => {
    if (!(mediaContainer instanceof HTMLElement)) return;

    stopOtherCardMedia(mediaContainer);

    const video = mediaContainer.querySelector('video[data-reel-video]');

    if (video instanceof HTMLVideoElement) {
      try {
        video.controls = true;
        await video.play();
        setMediaPlayingState(mediaContainer, true);
      } catch (error) {
        video.controls = false;
      }

      return;
    }

    const externalContainer = mediaContainer.querySelector('.video-reel-card__external');
    const template = mediaContainer.querySelector('template[data-reel-external-template]');

    if (externalContainer instanceof HTMLElement && template instanceof HTMLTemplateElement && !externalContainer.querySelector('iframe')) {
      externalContainer.appendChild(template.content.cloneNode(true));
    }

    if (externalContainer instanceof HTMLElement && externalContainer.querySelector('iframe')) {
      setMediaPlayingState(mediaContainer, true);
    }
  };

  const clampQuantityToRules = (input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const min = Number(input.getAttribute('min')) || 1;
    const step = Number(input.getAttribute('step')) || 1;
    const max = input.hasAttribute('max') ? Number(input.getAttribute('max')) : null;
    const rawValue = Number(input.value);
    let nextValue = Number.isFinite(rawValue) ? rawValue : min;

    if (nextValue < min) {
      nextValue = min;
    }

    const remainder = (nextValue - min) % step;

    if (remainder !== 0) {
      nextValue -= remainder;
      if (nextValue < min) {
        nextValue = min;
      }
    }

    if (max !== null && Number.isFinite(max) && nextValue > max) {
      const availableSteps = Math.floor((max - min) / step);
      nextValue = min + Math.max(0, availableSteps) * step;
    }

    input.value = String(nextValue);
  };

  const updateVariantState = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    if (!option) return;

    const form = select.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    const price = form.closest('.video-reel-card__product')?.querySelector('[data-reel-price]');
    const quantityInput = form.querySelector('[data-reel-quantity]');
    const submitButton = form.querySelector('[data-product-add-to-cart-button]');
    const isAvailable = option.dataset.available === 'true';

    if (price) {
      price.textContent = option.dataset.price || '';
    }

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.setAttribute('step', option.dataset.step || '1');
      quantityInput.setAttribute('min', option.dataset.min || '1');

      if (option.dataset.max) {
        quantityInput.setAttribute('max', option.dataset.max);
      } else {
        quantityInput.removeAttribute('max');
      }

      if (!quantityInput.value || Number(quantityInput.value) < Number(option.dataset.min || '1')) {
        quantityInput.value = option.dataset.min || '1';
      }

      clampQuantityToRules(quantityInput);
    }

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !isAvailable;
    }
  };

  const updateCarouselNavVisibility = (root = document) => {
    root.querySelectorAll('[data-video-reel-carousel]').forEach((carousel) => {
      if (!(carousel instanceof HTMLElement)) return;

      const track = carousel.querySelector('[data-carousel-track]');
      const nav = carousel.parentElement?.querySelector('.video-reel-carousel__nav');

      if (!(track instanceof HTMLElement) || !(nav instanceof HTMLElement)) return;

      const hasOverflow = track.scrollWidth - track.clientWidth > 8;
      const isDesktop = window.matchMedia('(min-width: 741px)').matches;

      nav.hidden = !(isDesktop && hasOverflow);
    });
  };

  const scheduleCarouselNavVisibilityUpdate = () => {
    if (navUpdateScheduled) return;

    navUpdateScheduled = true;

    window.requestAnimationFrame(() => {
      navUpdateScheduled = false;
      updateCarouselNavVisibility(document);
    });
  };

  const openReelModal = (button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('.video-reel-card');
    const mediaContainer = button.closest(MEDIA_SELECTOR);
    const modalId = button.getAttribute('aria-controls');
    const modal = modalId ? document.getElementById(modalId) : null;
    const modalBody = modal?.querySelector('[data-reel-modal-body]');
    const template = card?.querySelector('template[data-reel-modal-template]');

    if (!(modal instanceof HTMLElement) || !(modalBody instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) {
      return;
    }

    stopCardMedia(mediaContainer);
    modalBody.innerHTML = '';
    modalBody.appendChild(template.content.cloneNode(true));
    modal.open = true;
  };

  const cleanupModalMedia = (modal) => {
    if (!(modal instanceof HTMLElement)) return;

    const modalBody = modal.querySelector('[data-reel-modal-body]');
    if (!(modalBody instanceof HTMLElement)) return;

    modalBody.querySelectorAll('video').forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    });

    modalBody.querySelectorAll('iframe').forEach((iframe) => {
      if (iframe instanceof HTMLIFrameElement) {
        iframe.src = 'about:blank';
      }
    });

    modalBody.innerHTML = '';
  };

  const observeReelModals = (root = document) => {
    root.querySelectorAll('.video-reel-modal').forEach((modal) => {
      if (!(modal instanceof HTMLElement) || modal.dataset.reelObserved === 'true') return;

      modal.dataset.reelObserved = 'true';

      const observer = new MutationObserver(() => {
        if (!modal.hasAttribute('open')) {
          cleanupModalMedia(modal);
        }
      });

      observer.observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  };

  const handleCarouselNav = (button, direction) => {
    const controlsId = button.getAttribute('aria-controls');
    if (!controlsId) return;

    const track = document.getElementById(controlsId);
    if (!(track instanceof HTMLElement)) return;

    const firstCard = track.querySelector('.video-reel-card');
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0');
    const offset = (firstCard instanceof HTMLElement ? firstCard.offsetWidth : track.clientWidth * 0.8) + gap;

    track.scrollBy({
      left: offset * direction,
      behavior: 'smooth'
    });
  };

  const bindNativeVideoEvents = (root = document) => {
    root.querySelectorAll('video[data-reel-video]').forEach((video) => {
      if (!(video instanceof HTMLVideoElement) || video.dataset.reelBound === 'true') return;

      video.dataset.reelBound = 'true';

      video.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLVideoElement)) return;

        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', () => {
        const mediaContainer = video.closest(MEDIA_SELECTOR);
        stopOtherCardMedia(mediaContainer);
        setMediaPlayingState(mediaContainer, true);
      });

      ['pause', 'ended'].forEach((eventName) => {
        video.addEventListener(eventName, () => {
          const mediaContainer = video.closest(MEDIA_SELECTOR);

          if (eventName === 'ended') {
            video.currentTime = 0;
            video.controls = false;
          }

          setMediaPlayingState(mediaContainer, false);
        });
      });
    });
  };

  document.addEventListener('click', (event) => {
    const playButton = event.target.closest('[data-reel-play]');
    if (playButton instanceof HTMLButtonElement) {
      const mediaContainer = playButton.closest(MEDIA_SELECTOR);
      startMedia(mediaContainer);
      return;
    }

    const expandButton = event.target.closest('[data-reel-expand]');
    if (expandButton instanceof HTMLButtonElement) {
      openReelModal(expandButton);
      return;
    }

    const prevButton = event.target.closest('[data-carousel-prev]');
    if (prevButton instanceof HTMLButtonElement) {
      handleCarouselNav(prevButton, -1);
      return;
    }

    const nextButton = event.target.closest('[data-carousel-next]');
    if (nextButton instanceof HTMLButtonElement) {
      handleCarouselNav(nextButton, 1);
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target;

    if (select instanceof HTMLSelectElement && select.matches('[data-reel-variant-select]')) {
      updateVariantState(select);
      return;
    }

    if (select instanceof HTMLInputElement && select.matches('[data-reel-quantity]')) {
      clampQuantityToRules(select);
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    bindNativeVideoEvents(event.target);
    updateCarouselNavVisibility(event.target);
    observeReelModals(event.target);
  });

  document.addEventListener('close', (event) => {
    const modal = event.target;
    if (!(modal instanceof HTMLElement) || !modal.matches('.video-reel-modal')) return;

    cleanupModalMedia(modal);
  }, true);

  window.addEventListener('resize', scheduleCarouselNavVisibilityUpdate, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindNativeVideoEvents(document);
      observeReelModals(document);
      document.querySelectorAll('[data-reel-variant-select]').forEach((select) => updateVariantState(select));
      updateCarouselNavVisibility(document);
    }, { once: true });
  } else {
    bindNativeVideoEvents(document);
    observeReelModals(document);
    document.querySelectorAll('[data-reel-variant-select]').forEach((select) => updateVariantState(select));
    updateCarouselNavVisibility(document);
  }
})();
